
import api from '@/libs/api'
import moment from 'moment-timezone'
import Notify from 'quasar/src/plugins/Notify.js';
import alert2 from '@/assets/sounds/alert2_2.wav'

const CLEAR_INITIAL_HISTORY = 'CLEAR_INITIAL_HISTORY'
const DELETE_NOTIFICATION = 'DELETE_NOTIFICATION'
const SET_APPOINTMENT = 'SET_APPOINTMENT'
const SET_NOTIFICATION = 'SET_NOTIFICATION'

const CLEAR_TIMER = 'CLEAR_TIMER'
const CLEAR_ALL_TIMERS = 'CLEAR_ALL_TIMERS'
const STORE_TIMER = 'STORE_TIMER'

const TOOGLE_NOTIFICATIONS = 'TOOGLE_NOTIFICATIONS'

moment.locale('pt-br')
const formatToDate = (t) => moment.unix(t).format('YYYY-MM-DD HH:mm:ss')
const formatToUnix = (t) => moment(t).unix()

const getDefaultState = () => ({
  appointments: [],
  notifications: {},
  timers: {},
  notificationMode: true
})

export default {
  namespaced: true,

  state: getDefaultState(),

  getters: {
    appointments: state => state.appointments,
    notifications: state => state.notifications,
    timers: state => state.timers,
    notificationMode: state => state.notificationMode,
    todayNotifications: state => {
      const now = moment()
      const startOfToday = now.clone().startOf('day').unix()
      const endOfToday = now.clone().endOf('day').unix()
      if (!state.notifications) return []
      return Object.entries(state.notifications)
        .filter(([_, notif]) => notif.start >= startOfToday && notif.start <= endOfToday)
        .map(([id, notif]) => ({
          id,
          ...notif
        })).sort((a, b) => a.start - b.start)
    }
  },

  actions: {

    clearHistory ({ commit }) {
      commit(CLEAR_ALL_TIMERS)
      commit(CLEAR_INITIAL_HISTORY)
    },

    async deleteAppointments ({ dispatch, commit, state }, { idArray }) {
      await api.patchAppointments({ deleteIds: idArray })
        .then(() => {
          idArray.forEach(id => {
            commit(SET_APPOINTMENT, { appointments: id, operation: 'delete' })
            commit(CLEAR_TIMER, { id })
            const notification = state.notifications[id]
            if (notification.templateToSend) { dispatch('socketModule/deleteScheduledTemplate', { scheduleId: id }, { root: true }) }
          })

          commit(DELETE_NOTIFICATION, { deletedIds: idArray })
        })
        .catch((err) => { console.log(err) })

      return state.appointments
    },

    getAppointments ({ commit, state, dispatch }, params) {
      if (!state.appointments) state.appointments = []
      if (state.appointments && state.appointments.length) {
        mapAppointments(commit, state.appointments, false)
        dispatch('setInitialTimers')
        return state.appointments
      }

      return api.getAppointments(params)
        .then((events) => {
          const newEvents = events.response
          const appointments = mapAppointments(commit, newEvents, true)
          commit(SET_APPOINTMENT, { initialState: true, appointments })
          dispatch('setInitialTimers')
          return appointments
        })
        .catch((err) => { console.log(err) })
    },

    async patchAppointments ({ commit, state, dispatch }, appointment) {
      const configs = appointment.configs
      const templateConfigs = configs?.templateToSend
      const payload = {
        id: appointment.id,
        startDate: appointment.startDate,
        endDate: appointment.endDate,
        configs: appointment.configs,
        contact: appointment.contact,
        templateId: templateConfigs?.id
      }

      if (templateConfigs) {
        delete configs.templateToSend.message
        delete configs.templateToSend.params
        delete configs?.templateToSend?.header
      }

      payload.configs = JSON.stringify(configs)

      await api.patchAppointments(payload)
        .then(() => {
          commit(SET_APPOINTMENT, { appointments: appointment, operation: 'update' })
          commit(DELETE_NOTIFICATION, { deletedIds: [appointment.id] })
          commit(CLEAR_TIMER, { id: appointment.id })

          commit(SET_NOTIFICATION, {
            id: appointment.id,
            notificationTimeBefore: configs.notificationTimeBefore,
            start: formatToUnix(appointment.start),
            notificationTitle: configs.title,
            templateToSend: configs?.templateToSend
          })

          dispatch('setTimer', {
            id: appointment.id,
            notificationTimeBefore: configs.notificationTimeBefore,
            start: formatToUnix(appointment.start),
            notificationTitle: configs.title
          })

          if (configs?.templateToSend) {
            dispatch('socketModule/scheduleTemplate', {
              scheduleId: appointment.id,
              phoneNumber: appointment.contact,
              templateToSend: templateConfigs,
              start: formatToUnix(appointment.start)
            }, { root: true })
          }
        })
        .catch((err) => {
          console.log(err)
          if (err.response?.data?.$errors === 'appointment_already_exists') {
            Notify.create({
              message: 'Este contato já possui um agendamento neste horário. Por favor, escolha outro horário.',
              textColor: 'white',
              position: 'bottom',
              icon: 'mdi-alert',
              timeout: 5000,
              color: 'negative'
            })
          }
        })
      return state.appointments
    },

    async saveAppointment ({ commit, dispatch }, appointment) {
      const configs = appointment.configs
      const templateConfigs = { ...configs.templateToSend }

      if (templateConfigs.id) {
        delete configs?.templateToSend?.message
        delete configs?.templateToSend?.params
        delete configs?.templateToSend?.header
      }

      appointment.configs = JSON.stringify(configs)

      return api.saveAppointment(appointment).then(() => {
        const newEvent = {
          id: appointment.id,
          configs: appointment.configs,
          start: formatToDate(appointment.startDate),
          end: formatToDate(appointment.endDate),
          editable: true,
          attendanceInfo: appointment.attendanceInfo,
          backgroundColor: configs.backgroundColor,
          borderColor: configs.backgroundColor,
          title: configs.title,
          templateToSend: templateConfigs,
          contact: appointment.contact
        }

        commit(SET_APPOINTMENT, { appointments: newEvent, operation: 'add' })

        commit(SET_NOTIFICATION, {
          id: appointment.id,
          notificationTimeBefore: configs.notificationTimeBefore,
          start: appointment.startDate,
          notificationTitle: configs.title,
          templateToSend: templateConfigs
        })

        dispatch('setTimer', {
          id: appointment.id,
          notificationTimeBefore: configs.notificationTimeBefore,
          start: appointment.startDate,
          notificationTitle: configs.title
        })

        if (templateConfigs.id) {
          dispatch('socketModule/scheduleTemplate', {
            scheduleId: appointment.id,
            phoneNumber: appointment.contact,
            templateToSend: templateConfigs,
            start: appointment.startDate
          }, { root: true })
        }
      }).catch((err) => {
        console.log({ err })
        if (err.response?.data?.$errors === 'appointment_already_exists') {
          Notify.create({
            message: 'Este contato já possui um agendamento neste horário. Por favor, escolha outro horário.',
            textColor: 'white',
            position: 'bottom',
            icon: 'mdi-alert',
            timeout: 5000,
            color: 'negative'
          })
        }
      })
    },

    setInitialTimers ({ state, dispatch }) {
      if (!state.notifications) state.notifications = {}
      for (const id of Object.keys(state.notifications)) {
        const { notificationTimeBefore, start, notificationTitle, attendanceInfo } = state.notifications[id]
        dispatch('setTimer', { id, notificationTimeBefore, start, notificationTitle, attendanceInfo })
      }
    },

    setTimer ({ state, commit, rootGetters, rootState }, { id, notificationTimeBefore, start, notificationTitle }) {
      if (!state.timers) state.timers = {}
      const alertIcon = 'mdi-calendar-alert'

      if (!notificationTimeBefore) return

      if (!state.notificationMode) return
      const now = Date.now()
      const triggerTime = start * 1000 - notificationTimeBefore * 1000
      const delay = triggerTime - now

      if (delay <= 0) return

      if (state.timers[id]) { clearTimeout(state.timers[id]) }

      const timeoutId = setTimeout(async () => {
        const minutesBefore = notificationTimeBefore / 60
        const notificationPreference = rootGetters['settingsModule/notificationStyle']
        const agentStatus = rootState.agentModule?.session?.agent?.status
        const isLogged = Boolean(agentStatus && agentStatus !== 'OFFLINE')
        if (!isLogged) return

        switch (notificationPreference) {
          case 'sound':
            playNotificationSound(rootGetters)
            break

          case 'popup':
            Notify.create({
              message: `Evento agendado em ${minutesBefore} minutos`,
              textColor: 'black',
              caption: notificationTitle,
              icon: alertIcon,
              position: 'bottom',
              timeout: 3000,
              closeBtn: true,
              classes: 'notification-popup'
            })
            break

          default:
            playNotificationSound(rootGetters)
            Notify.create({
              message: `Evento agendado em ${minutesBefore} minutos`,
              textColor: 'black',
              caption: notificationTitle,
              icon: alertIcon,
              position: 'bottom',
              timeout: 3000,
              closeBtn: true,
              classes: 'notification-popup'
            })
            break
        }

        clearTimeout(state.timers[id])
      }, delay)

      commit(STORE_TIMER, { id, timeoutId })
    },

    async setAppointment ({ commit }, appointment) {
      return api.setAppointment(appointment).then(() => {
        commit(SET_APPOINTMENT, appointment)
      }).catch((err) => { console.log(err) })
    },

    showNotifications ({ commit, dispatch }, { notificationMode }) {
      commit(TOOGLE_NOTIFICATIONS, { notificationMode })

      if (!notificationMode) {
        commit(CLEAR_ALL_TIMERS)
      }

      if (notificationMode) {
        dispatch('setInitialTimers')
      }
    }

  },

  mutations: {
    [STORE_TIMER] (state, { id, timeoutId }) {
      state.timers = {
        ...state.timers,
        [id]: timeoutId
      }
    },

    [CLEAR_TIMER] (state, { id }) {
      clearTimeout(state.timers[id])
      delete state.timers[id]
    },

    [CLEAR_ALL_TIMERS] (state) {
      for (const id in state.timers) {
        clearTimeout(state.timers[id])
      }
      state.timers = {}
    },

    [CLEAR_INITIAL_HISTORY] (state) {
      Object.assign(state, getDefaultState())
    },

    [SET_APPOINTMENT] (state, { initialState, appointments, operation = '' }) {
      if (initialState) {
        state.appointments = Array.isArray(appointments) ? appointments : [...appointments]
        return
      }

      switch (operation) {
        case 'add':
          state.appointments.push(appointments)
          break
        case
          'delete': {
            const deletedId = appointments
            state.appointments = state.appointments.filter((event) => event.id !== deletedId)
          }
          break
        case 'update': {
          appointments.start = formatToDate(appointments.startDate)
          appointments.end = formatToDate(appointments.endDate)
          delete appointments.startDate
          delete appointments.endDate

          const index = state.appointments.findIndex(event => event.id === appointments.id)

          const updatedAppointments = [...state.appointments]
          const configs = appointments.configs
          appointments.title = configs.title
          appointments.description = configs.description
          appointments.backgroundColor = configs.backgroundColor
          appointments.borderColor = configs.backgroundColor
          appointments.templateToSend = configs.templateToSend

          updatedAppointments[index] = { ...updatedAppointments[index], ...appointments }
          state.appointments = updatedAppointments
        }
          break
        default:
          break
      }
      return state.appointments
    },

    [SET_NOTIFICATION] (state, { id, notificationTimeBefore, start, notificationTitle, templateToSend }) {
      let notifications = { ...state.notifications }
      notifications = {
        ...notifications,
        [id]: {
          notificationTimeBefore,
          start,
          notificationTitle,
          templateToSend
        }
      }
      state.notifications = notifications
    },

    [DELETE_NOTIFICATION] (state, { deletedIds }) {
      const notifications = { ...state.notifications }
      deletedIds.forEach((id) => { delete notifications[id] })
      state.notifications = notifications
    },

    [TOOGLE_NOTIFICATIONS] (state, { notificationMode }) {
      state.notificationMode = notificationMode
    }

  }

}

const playNotificationSound = async (rootGetters) => {
  const attendances = rootGetters['attendanceModule/getAttendances']
  const additionalDevice = rootGetters['settingsModule/additionalDevice']
  const hasVoiceAttendance = Object.values(attendances).filter(({ channel }) => channel === 'VOICE').length > 0
  const audio = new Audio(alert2)
  await audio.setSinkId(additionalDevice)
  if (!hasVoiceAttendance) audio.play()
}

const mapAppointments = (commit, appointments, setNotifications) => {
  const newEvents = appointments.map((appointment) => {
    const parsedConfigs = parseVar(appointment.configs)

    if (setNotifications) {
      commit(SET_NOTIFICATION, {
        id: appointment.id,
        notificationTimeBefore: parsedConfigs.notificationTimeBefore,
        start: appointment.start_date ?? moment(appointment.start).unix(),
        notificationTitle: parsedConfigs?.title,
        attendanceInfo: appointment.attendance_info,
        templateToSend: parsedConfigs?.templateToSend
      })
    }

    return {
      id: appointment.id,
      title: parsedConfigs?.title,
      description: parsedConfigs?.description,
      contact: appointment.contact,
      start: formatToDate(appointment.start_date),
      end: formatToDate(appointment.end_date),
      attendanceInfo: appointment.attendance_info,
      configs: parsedConfigs,
      editable: true,
      backgroundColor: parsedConfigs?.backgroundColor,
      borderColor: parsedConfigs?.backgroundColor,
      templateToSend: parsedConfigs?.templateToSend
    }
  })
  return newEvents
}

const parseVar = (e) => {
  if (!e) return {}

  try {
    return JSON.parse(e)
  } catch {
    return e
  }
}
